import React, { Component } from 'react';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import fragments from 'utils/graphql/fragments';
import { displayUserShape, errorShape } from 'utils/shapes';

const MAX_PENDING_LENGTH = 1000;
const MAX_USER_LENGTH = 1000;
const ANIMATION_DURATION = 15000;
const MIN_ANIMATION_RATE = 500;
const BUTTON_DELAY_MS = 500;

const withActiveUsersData =
  ({ usersPerPage }) =>
  BaseComponent => {
    class ActiveUsersContainer extends Component {
      constructor(props) {
        super(props);

        this.state = {
          users: [],
          offset: 0
        };

        this.initialized = false;
        this.pendingUsers = [];
        this.animationTimeoutId = null;
        this.animationRate = 0;
        this.buttonTimer = 0;

        this.handleAddedUsers = this.handleAddedUsers.bind(this);
        this.handleRemovedUsers = this.handleRemovedUsers.bind(this);
        this.triggerAnimation = this.triggerAnimation.bind(this);
        this.animatePendingUsers = this.animatePendingUsers.bind(this);
        this.handleNextButton = this.handleNextButton.bind(this);
        this.handlePrevButton = this.handlePrevButton.bind(this);
        this.unsubscribeFromNewData = () => {};
      }

      componentDidMount() {
        this.unsubscribeFromNewData = this.props.subscribeToNewData(
          this.handleAddedUsers,
          this.handleRemovedUsers
        );
      }

      componentWillUnmount() {
        clearTimeout(this.animationTimeoutId);

        this.unsubscribeFromNewData();
        this.unsubscribeFromNewData = () => {};
      }

      componentWillReceiveProps(nextProps) {
        const next = nextProps.data && nextProps.data.activeUsers;
        if (
          !this.initialized &&
          !nextProps.data.loading &&
          next &&
          next.users
        ) {
          // Add the initial group of users from the query
          this.initialized = true;
          this.initializeUsers(next.users);
        }
      }

      initializeUsers(users) {
        const clampedUsers = users.slice(0, MAX_USER_LENGTH);
        this.setState({
          users: clampedUsers,
          offset: 0
        });

        this.pendingUsers = this.pendingUsers.filter(
          user => !clampedUsers.find(u => u.userId === user.userId)
        );

        if (this.pendingUsers.length) {
          this.triggerAnimation();
        }
      }

      handleAddedUsers(newUsers) {
        if (!newUsers || !newUsers.length) {
          return;
        }

        const uniqueUsers = newUsers.filter(
          user =>
            !this.pendingUsers.find(u => u.userId === user.userId) &&
            !this.state.users.find(u => u.userId === user.userId)
        );
        this.pendingUsers = this.pendingUsers
          .concat(uniqueUsers)
          .slice(-MAX_PENDING_LENGTH);

        this.triggerAnimation();
      }

      handleRemovedUsers(removedUserIds) {
        if (!removedUserIds || !removedUserIds.length) {
          return;
        }
        this.pendingUsers = this.pendingUsers.filter(
          user => !removedUserIds.find(id => id === user.userId)
        );

        let newOffset = this.state.offset;
        const filteredUsers = this.state.users.filter((user, index) => {
          const remove = removedUserIds.find(id => id === user.userId);
          if (remove && index < this.state.offset) {
            newOffset -= 1;
          }
          return !remove;
        });
        const lastOffset = filteredUsers.length - usersPerPage;
        newOffset = Math.max(Math.min(newOffset, lastOffset), 0);
        this.setState({
          users: filteredUsers,
          offset: newOffset
        });
      }

      triggerAnimation() {
        if (!this.initialized) {
          return;
        }
        if (this.animationTimeoutId) {
          clearTimeout(this.animationTimeoutId);
        }

        this.animationRate = Math.max(
          MIN_ANIMATION_RATE,
          ANIMATION_DURATION / this.pendingUsers.length
        );
        this.animatePendingUsers();
      }

      animatePendingUsers() {
        this.animationTimeoutId = null;
        const next = this.pendingUsers.shift();
        if (!next) {
          return;
        }

        this.setState({
          users: [next].concat(this.state.users).slice(0, MAX_USER_LENGTH),
          offset: this.state.offset && this.state.offset + 1
        });

        if (this.pendingUsers.length) {
          this.animationTimeoutId = setTimeout(
            this.animatePendingUsers,
            this.animationRate
          );
        }
      }

      canScrollPrev() {
        return this.state.offset > 0;
      }

      canScrollNext() {
        return this.state.offset < this.state.users.length - usersPerPage;
      }

      handlePrevButton() {
        if (Date.now() > this.buttonTimer && this.canScrollPrev()) {
          let offset = Math.max(0, this.state.offset - usersPerPage);
          this.setState({ offset: offset });
          this.buttonTimer = Date.now() + BUTTON_DELAY_MS;
        }
      }

      handleNextButton() {
        if (Date.now() > this.buttonTimer && this.canScrollNext()) {
          let offset = this.state.offset;
          const lastOffset = this.state.users.length - usersPerPage;
          offset = Math.min(lastOffset, offset + usersPerPage);
          this.setState({ offset: offset });
          this.buttonTimer = Date.now() + BUTTON_DELAY_MS;
        }
      }

      render() {
        const { users, offset } = this.state;
        const { data, subscribeToNewData, ...rest } = this.props;
        const count = data && data.activeUsers && data.activeUsers.totalUsers;
        const onPrevButton = this.canScrollPrev()
          ? this.handlePrevButton
          : undefined;
        const onNextButton = this.canScrollNext()
          ? this.handleNextButton
          : undefined;
        const activeUsers = {
          totalUsers: count,
          users: users,
          offset: offset
        };
        return (
          <BaseComponent
            {...rest}
            activeUsers={activeUsers}
            onPrevButton={onPrevButton}
            onNextButton={onNextButton}
          />
        );
      }
    }

    ActiveUsersContainer.propTypes = {
      data: PropTypes.shape({
        activeUsers: PropTypes.shape({
          totalUsers: PropTypes.number,
          users: PropTypes.arrayOf(displayUserShape)
        }),
        loading: PropTypes.bool,
        error: errorShape
      })
    };

    const ACTIVE_USERS_QUERY = gql`
      query {
        activeUsers {
          totalUsers
          users {
            ...UserIdAndDisplayFields
          }
        }
      }
      ${fragments.userIdAndDisplay}
    `;

    const ACTIVE_USERS_SUBSCRIPTION = gql`
      subscription {
        activeUsersUpdates {
          totalUsers
          addedUsers {
            ...UserIdAndDisplayFields
          }
          removedUserIds
        }
      }
      ${fragments.userIdAndDisplay}
    `;

    const withData = graphql(ACTIVE_USERS_QUERY, {
      options: { fetchPolicy: 'network-only' },
      props: ({ ownProps, data }) => {
        return {
          data,
          subscribeToNewData: (onAddedUsers, onRemovedUsers) => {
            return data.subscribeToMore({
              document: ACTIVE_USERS_SUBSCRIPTION,
              updateQuery: (prev, { subscriptionData }) => {
                const { totalUsers, addedUsers, removedUserIds } =
                  subscriptionData.data;
                if (addedUsers) {
                  onAddedUsers && onAddedUsers(addedUsers);
                }
                if (removedUserIds) {
                  onRemovedUsers && onRemovedUsers(removedUserIds);
                }
                if (totalUsers !== undefined && prev) {
                  return Object.assign({}, prev, {
                    activeUsers: Object.assign({}, prev.activeUsers, {
                      totalUsers: totalUsers
                    })
                  });
                }
                return null;
              }
            });
          }
        };
      }
    });

    return withData(ActiveUsersContainer);
  };

export default withActiveUsersData;
